import { DevicePlatform, Role } from '../../enums';
import { IVcfPermission, IVcfTrustedDevice, VcfDigitalKeyClient } from './VcfDigitalKeyClient';

export enum DigitalKeyState {
    ENDED,
    UNKNOWN,
    CONFIRMED,
    ACCEPTED,
    USED
}

export enum DigitalKeyRemovalState {
    INREMOVAL,
    REMOVALFAILED,
    UNKNOWN
}

export enum InstallationState {
    SUCCEEDED,
    FAILED,
    UNKNOWN
}

export interface IDigitalKey {
    readonly id?: string;
    readonly alias?: string;
    readonly userName?: string;
    readonly expirationTimestamp?: string;
    readonly state: DigitalKeyState;
    readonly removalState: DigitalKeyRemovalState;
    readonly installationState: InstallationState;
    readonly isOwnKey: boolean;
}

export interface ITrustedDevice {
    readonly id?: string;
    readonly primaryUserId?: string;
    readonly imei?: string;
}

export interface IDigitalKeyStatus {
    readonly userRole: Role;
    readonly freeMobileKeys: number;
    readonly trustedDevices: ITrustedDevice[];
}

export class VcfDigitalKeyService {
    constructor(private readonly vcfDigitalKeyClient: VcfDigitalKeyClient) {
    }

    public async getDigitalKeys(vin: string, devicePlatform: DevicePlatform): Promise<IDigitalKey[]> {
        const isWcar = devicePlatform === DevicePlatform.WCAR;
        const mobileKeysListResponse = await this.vcfDigitalKeyClient.getMobileKeysList(vin, isWcar);
        const mappedKeys = mobileKeysListResponse.data?.permissionList?.map(this.transformPermissionToDigitalKey) ?? []
        return mappedKeys.filter(this.filterKeysWithValidStates);
    }

    public async getDigitalKeyStatus(vin: string, devicePlatform: DevicePlatform): Promise<IDigitalKeyStatus> {
        const isWcar = devicePlatform === DevicePlatform.WCAR;
        const response = await this.vcfDigitalKeyClient.getMobileKeysStatus(vin, isWcar);
        return {
            trustedDevices: this.filterTrustedDevices(response.data?.trusteddevice ?? []),
            freeMobileKeys: response.data?.serviceStatus?.keyVoucherFreeCount ?? 0,
            userRole: response.data?.serviceStatus?.userRole ?? Role.UNKNOWN
        };
    }

    public async deleteTrustedDevice(id: string, vin: string, devicePlatform: DevicePlatform): Promise<void> {
        const isWcar = devicePlatform === DevicePlatform.WCAR;
        await this.vcfDigitalKeyClient.deleteTrustedDevice(id, vin, isWcar);
    }

    public async deleteDigitalKey(id: string, vin: string, devicePlatform: DevicePlatform): Promise<void> {
        const isWcar = devicePlatform === DevicePlatform.WCAR;
        await this.vcfDigitalKeyClient.deleteMobileKeyPermission(id, vin, isWcar);
    }

    public async renameDigitalKey(id: string, alias: string, vin: string, devicePlatform: DevicePlatform): Promise<void> {
        const isWcar = devicePlatform === DevicePlatform.WCAR;
        await this.vcfDigitalKeyClient.putMobileKeyPermission(id, alias, vin, isWcar);
    }

    private filterKeysWithValidStates(key: IDigitalKey): boolean {
        const validState1 = key.state === DigitalKeyState.CONFIRMED || key.state === DigitalKeyState.ACCEPTED;
        const validState2 = key.state === DigitalKeyState.USED && key.installationState === InstallationState.SUCCEEDED
        const validState3 = key.state === DigitalKeyState.ENDED
            && (key.removalState === DigitalKeyRemovalState.INREMOVAL || key.removalState === DigitalKeyRemovalState.REMOVALFAILED)
            && key.installationState === InstallationState.SUCCEEDED

        return validState1 || validState2 || validState3;
    }


    private filterTrustedDevices(trustedDevices: IVcfTrustedDevice[]): ITrustedDevice[] {
        return trustedDevices.filter(this.trustedDeviceIsRegistered).map(this.transformVcfTrustedDevice);
    }

    private transformPermissionToDigitalKey(permission: IVcfPermission): IDigitalKey {
        const {
            permissionId,
            frontendAlias,
            mkUserUserName,
            expirationTimestamp,
            state,
            removalState,
            mkAdministratorUserName,
            installationState
        } = permission;

        return {
            id: permissionId,
            alias: frontendAlias,
            userName: mkUserUserName,
            expirationTimestamp: expirationTimestamp,
            state: (DigitalKeyState as any)[state?.toUpperCase() ?? ''] ?? DigitalKeyState.UNKNOWN,
            installationState: (InstallationState as any)[installationState?.toUpperCase() ?? ''] ?? InstallationState.UNKNOWN,
            removalState: (DigitalKeyRemovalState as any)[removalState?.toUpperCase() ?? ''] ?? DigitalKeyRemovalState.UNKNOWN,
            isOwnKey: mkUserUserName ? VcfDigitalKeyService.equalsIgnoreCase(mkUserUserName, mkAdministratorUserName) : false,
        };
    }

    private trustedDeviceIsRegistered(vcfTrustedDevice: IVcfTrustedDevice): boolean {
        return vcfTrustedDevice.status === 'REGISTERED';
    }

    private transformVcfTrustedDevice(vcfTrustedDevice: IVcfTrustedDevice): ITrustedDevice {
        const {teeId, primaryUserID, imei} = vcfTrustedDevice;
        return {id: teeId, primaryUserId: primaryUserID, imei};
    }

    private static equalsIgnoreCase(str1: string, str2?: string) {
        if (str1 && str2) {
            return str1.localeCompare(str2, undefined, {sensitivity: 'base'}) === 0
        }
        return false;
    };

}